import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useApi } from '@/helpers/udab-api';

const userToken = ref(null);
const isInitialized = ref(false);

const LOGIN_DESTINATION_KEY = 'aiq_login_destination';
const IN_APP_PATH = /^\/(?![/\\])/;

export const rememberLoginDestination = (path) => {
  if (IN_APP_PATH.test(path) && path !== '/' && !path.startsWith('/auth')) {
    sessionStorage.setItem(LOGIN_DESTINATION_KEY, path);
  }
};

export const takeLoginDestination = () => {
  const path = sessionStorage.getItem(LOGIN_DESTINATION_KEY);
  sessionStorage.removeItem(LOGIN_DESTINATION_KEY);
  return IN_APP_PATH.test(path || '') ? path : '/';
};

export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const isTokenValid = async (token) => {
  try {
    const payload = parseJwt(token);
    if (!payload) return false;

    const expiryTime = payload.exp * 1000;
    return Date.now() < expiryTime;
  } catch (e) {
    return false;
  }
};

export const ensureTokenParsed = () => {
  if (!userToken.value) {
    const token = localStorage.getItem('aiq_token');
    if (token) {
      userToken.value = parseJwt(token);
    }
  }
};

export const hasPermission = (permission) => {
  if (!userToken.value) return false;
  if (userToken.value.superadmin) return true;
  if (!userToken.value.permissions) return false;
  return userToken.value.permissions.includes(permission);
};

export const hasAnyPermission = (permissions) => {
  if (!userToken.value) return false;
  if (userToken.value.superadmin) return true;
  if (!userToken.value.permissions) return false;
  return permissions.some(permission => userToken.value.permissions.includes(permission));
};

export const useAuth = () => {
  const router = useRouter();
  const api = useApi();

  const initAuth = async () => {
    if (isInitialized.value) return userToken.value;

    const token = localStorage.getItem('aiq_token');
    if (!token || !(await isTokenValid(token))) {
      userToken.value = null;
      return null;
    }

    userToken.value = parseJwt(token);
    isInitialized.value = true;
    return userToken.value;
  };

  const getUserInitials = () => {
    if (!userToken.value || !userToken.value.first_name || !userToken.value.last_name) {
      return '';
    }
    return userToken.value.first_name.charAt(0).toUpperCase() + userToken.value.last_name.charAt(0).toUpperCase();
  };

  const logout = () => {
    document.querySelectorAll('div.bs-tooltip-auto[role="tooltip"]').forEach(el => el.remove());
    localStorage.removeItem('aiq_token');
    userToken.value = null;
    router.push('/auth/login');
  };

  const isImpersonating = computed(() => {
    return userToken.value && 'impersonated_by' in userToken.value;
  });

  const getOriginalUser = computed(() => {
    if (isImpersonating.value) {
      return userToken.value.original_user;
    }
    return null;
  });

  const impersonateUser = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/impersonate`);
      if (response && response.success) {
        localStorage.setItem('aiq_token', response.token);
        userToken.value = parseJwt(response.token);
        return true;
      }
    } catch (error) {
      console.error('Failed to impersonate user:', error);
    }
    return false;
  };

  const exitImpersonation = async () => {
    if (!isImpersonating.value) return false;

    try {
      const response = await api.post('/users/exit-impersonation');
      if (response && response.success) {
        localStorage.setItem('aiq_token', response.token);
        userToken.value = parseJwt(response.token);
        return true;
      }
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
    }
    return false;
  };

  return {
    userToken,
    initAuth,
    getUserInitials,
    logout,
    hasPermission,
    hasAnyPermission,
    isImpersonating,
    getOriginalUser,
    impersonateUser,
    exitImpersonation
  };
};
